import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ClipboardCheck, PlayCircle, Search, Settings2, X } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EtagereVisuel } from "@/components/depot/EtagereVisuel";
import { GestionEtageres } from "@/components/depot/GestionEtageres";
import { InfoFichier, correspond } from "@/components/depot/InfoFichier";
import { EcheanceBadge } from "@/components/colis/Echeance";
import { CapteurLecteur } from "@/components/colis/ChampLecteur";
import { fmtDateHeure } from "@/components/scans/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { lireCode, numeroLisible } from "@/lib/colisCode";
import { peut } from "@/lib/permissions";
import { colisSubnav } from "@/lib/subnavs";
import { StatutColis } from "./ColisPage";

const P = { params: { zone: "cage" } };

/**
 * Colis > Colis cage : les étagères de la cage, toujours affichées (même
 * vides), avec le contenu du dernier inventaire validé de la cage.
 */
export default function CagePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [etat, setEtat] = useState(null);
  const [enCours, setEnCours] = useState(undefined);
  const [invId, setInvId] = useState("");
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const [gestion, setGestion] = useState(false);
  const detail = useRef(null);
  const choisir = (id) => {
    setSel(id);
    if (window.innerWidth < 1024) setTimeout(() => detail.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const charger = useCallback(async () => {
    try {
      const [e, c] = await Promise.all([
        api.get("/depot/etat", { params: { zone: "cage", ...(invId ? { inventaire_id: invId } : {}) } }),
        api.get("/depot/inventaire", P),
      ]);
      setEtat(e.data); setEnCours(c.data);
    } catch (err) { toast.error(errMsg(err)); }
  }, [invId]);
  useEffect(() => { charger(); }, [charger]);

  const etageres = useMemo(() => etat?.chariots || [], [etat]);
  const anciens = (etat?.historique || []).filter((h) => !(etat?.du_jour && h.id === etat.inventaire?.id) && h.id !== invId);
  const emplacements = useMemo(() => etageres.flatMap((ch) => ch.emplacements.map((e) => ({ ...e, chariot: ch }))), [etageres]);
  const total = emplacements.reduce((n, e) => n + e.colis.length, 0);
  const recherche = q.trim().length >= 3 ? q.trim() : "";
  const trouves = useMemo(() => (recherche
    ? emplacements.flatMap((e) => e.colis.filter((c) => correspond(c, recherche)).map((c) => ({ ...c, emp: e })))
    : []), [emplacements, recherche]);
  const surligne = [...new Set(trouves.map((t) => t.emp.id))];
  const selEmp = emplacements.find((e) => e.id === sel);

  return (
    <AppShell title="Colis cage" subnav={colisSubnav()}>
      <CapteurLecteur onScan={(code) => { const lu = lireCode(code); setQ(lu.type ? lu.numero : code); }} actif={!gestion} />
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            Colis cage
            {peut(user, "cage_etageres") && (
              <button onClick={() => setGestion(true)} className="text-xs font-normal px-2 py-1 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent inline-flex items-center gap-1" data-testid="btn-gerer-etageres">
                <Settings2 size={12} /> Gérer les étagères
              </button>
            )}
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="cage-info">
            {!etat ? "Chargement…" : etat.inventaire
              ? <>{total} colis · inventaire validé le {fmtDateHeure(etat.inventaire.fin)} par {etat.inventaire.valide_par?.nom || "—"}</>
              : <>Pas encore d'inventaire de la cage aujourd'hui (remise à zéro chaque nuit à {etat.remise_a_zero || "1 h"}).</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {anciens.length > 0 && (
            <select value={invId} onChange={(e) => { setInvId(e.target.value); setSel(null); }} className="h-9 rounded-md border border-input bg-transparent px-2 text-sm" data-testid="cage-historique">
              <option value="">Aujourd'hui</option>
              {anciens.map((h) => <option key={h.id} value={h.id}>Inventaire du {fmtDateHeure(h.fin)}</option>)}
            </select>
          )}
          {peut(user, "cage_inventaire") && <Button onClick={() => navigate("/colis/cage/inventaire")} data-testid="btn-inventaire-cage">
            {enCours ? <PlayCircle size={15} /> : <ClipboardCheck size={15} />} {enCours ? `Reprendre l'inventaire (${enCours.nb})` : "Inventaire de la cage"}
          </Button>}
        </div>
      </div>

      <div className="relative sm:w-80 mb-5">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="N° de colis, expéditeur ou destinataire…" className="pl-9" data-testid="cage-recherche" />
        {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground" aria-label="Effacer"><X size={14} /></button>}
      </div>
      {recherche && (
        <div className="mb-5 rounded-xl border border-border bg-card p-3" data-testid="cage-resultats">
          {trouves.length === 0 ? <p className="text-sm text-muted-foreground">Aucun colis « {q} » dans la cage.</p> : (
            <ul className="divide-y divide-border text-sm">
              {trouves.slice(0, 50).map((t) => (
                <li key={t.numero} className="py-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-bold">{numeroLisible(t.numero)}</span>
                    <span className="text-muted-foreground">→</span>
                    <button className="font-semibold text-primary hover:underline" onClick={() => choisir(t.emp.id)}>{t.emp.nom}</button>
                    {t.fiche && <StatutColis c={t.fiche} detail />}
                  </div>
                  <InfoFichier info={t.info} compact recherche={recherche} numero={t.numero} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {etat && etageres.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="cage-vide">
          Aucune étagère. {peut(user, "cage_etageres") ? "Cliquez sur « Gérer les étagères » pour les créer." : "Un responsable doit d'abord créer les étagères."}
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[1fr_360px] items-start">
          <div className="space-y-6 min-w-0" data-testid="cage-etageres">
            {etageres.map((et) => (
              <EtagereVisuel key={et.id} lettre={et.lettre} lignes={et.lignes} colonnes={et.colonnes} emplacements={et.emplacements}
                selection={sel} surligne={surligne} onSelect={(e) => choisir(e.id)} testId={`etagere-${et.lettre}`} />
            ))}
          </div>
          <aside ref={detail} className="rounded-xl border border-border bg-card p-4 lg:sticky lg:top-4 scroll-mt-4" data-testid="cage-detail">
            {!selEmp ? <p className="text-sm text-muted-foreground">Touchez une place de l'étagère pour voir ses colis.</p> : (
              <>
                <p className="font-heading font-semibold">{selEmp.nom}</p>
                <p className="text-xs text-muted-foreground mb-3">{selEmp.colis.length} colis · code {selEmp.code}</p>
                {selEmp.colis.length === 0 ? <p className="text-sm text-muted-foreground">Vide.</p> : (
                  <ul className="divide-y divide-border max-h-[60vh] overflow-y-auto">
                    {selEmp.colis.map((c) => (
                      <li key={c.numero} className={`py-2 text-sm ${correspond(c, recherche) ? "bg-red-500/10 -mx-2 px-2 rounded" : ""}`}>
                        <div className="flex items-center justify-between gap-2">
                          {c.fiche && peut(user, "colis_colis") ? <Link to={`/colis/${c.fiche.id}`} className="font-mono font-bold hover:underline">{numeroLisible(c.numero)}</Link>
                            : <span className="font-mono font-bold">{numeroLisible(c.numero)}</span>}
                          <span className="text-xs text-muted-foreground">{c.type_label}</span>
                        </div>
                        <InfoFichier info={c.info} recherche={recherche} numero={c.numero} />
                        {c.fiche && (
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            <StatutColis c={c.fiche} detail />
                            {c.fiche.echeance && <EcheanceBadge e={c.fiche.echeance} court />}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </aside>
        </div>
      )}
      {etat?.orphelins?.length > 0 && (
        <p className="mt-4 text-xs text-muted-foreground">{etat.orphelins.length} colis rangés sur une place supprimée depuis l'inventaire : {etat.orphelins.map((c) => c.numero).join(", ")}</p>
      )}
      {peut(user, "cage_etageres") && <GestionEtageres open={gestion} onClose={() => setGestion(false)} onChange={charger} />}
    </AppShell>
  );
}
